import React, { useEffect, useState, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import ReactModal from 'react-modal';

import { SelectionListItem } from '../../../contexts/types';
import ModalDesktop from './modal-desktop';
import ModalMobile from './modal-mobile';
import { MIN_WIDTH_DESKTOP } from '../../../constants.ts';

// this is required for accessibility of the modal
if (import.meta.env.MODE !== 'test') {
    ReactModal.setAppElement('#root');
}

type SelectionModalProps = {
    title: string;
    subtitle: string;
    items: SelectionListItem[];
    onAdd: (items: SelectionListItem[]) => void;
    onClose: () => void;
    fetchNextPage: () => void;
    isLoading: boolean;
    isFetchingNextPage: boolean;
    showModal: boolean;
};

const SelectionModal = ({
    items,
    title,
    subtitle,
    onAdd,
    onClose,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    showModal,
}: SelectionModalProps) => {
    const isDesktopView = useMediaQuery({ minWidth: MIN_WIDTH_DESKTOP });
    const prevItemsRef = useRef(items);

    // this state is used to keep track of the checked items until we actually add them
    const [itemsTemporary, setItemsTemporary] = useState(items);

    useEffect(() => {
        // when we lazy load new items, we reset the checked state of the items
        // we need to add it back to the itemsTemporary state here
        if (prevItemsRef.current.length !== items.length) {
            setItemsTemporary((prevItemsTemporary) => {
                const itemsWithChecked = items.map((item) => {
                    return {
                        ...item,
                        checked:
                            prevItemsTemporary.find((tempItem) => tempItem.id === item.id)
                                ?.checked || false,
                    };
                });

                return itemsWithChecked;
            });
        }

        prevItemsRef.current = items;
    }, [items]);

    const onToggle = (id: string) => {
        setItemsTemporary((prevItemsTemporary) => {
            return prevItemsTemporary.map((item) => {
                if (item.id === id) {
                    return {
                        ...item,
                        checked: !item.checked,
                    };
                }
                return item;
            });
        });
    };

    const handleAddItems = () => {
        const selectedItems = itemsTemporary.filter((item) => item.checked);
        onAdd(selectedItems);
        onClose();
    };

    return (
        <>
            {isDesktopView && showModal ? (
                <ModalDesktop
                    items={itemsTemporary}
                    title={title}
                    subtitle={subtitle}
                    onClose={onClose}
                    onAdd={handleAddItems}
                    onToggle={onToggle}
                    fetchNextPage={fetchNextPage}
                    isLoading={isLoading}
                    isFetchingNextPage={isFetchingNextPage}
                />
            ) : (
                <ModalMobile
                    items={itemsTemporary}
                    title={title}
                    subtitle={subtitle}
                    onClose={onClose}
                    onAdd={handleAddItems}
                    onToggle={onToggle}
                    fetchNextPage={fetchNextPage}
                    isLoading={isLoading}
                    isFetchingNextPage={isFetchingNextPage}
                    showModal={showModal}
                />
            )}
        </>
    );
};

export default SelectionModal;
